import React, { useEffect, useState } from "react";
import TailwindLoadingSpinner from "./TailwindLoadingSpinner";

/**
 * TailwindTable - improved visual fixes:
 * - single scroll container (max height) to avoid unwanted scrollbars
 * - search input colors fixed for dark/light
 * - thin scrollbar classes included (optional Tailwind plugin)
 * - td cell styles ensure consistent font, color, vertical alignment
 */
const TailwindTable = ({
  columns = [],
  data = [],
  renderCell,
  isSelected = null,
  searchFilter = false,
  searchAction,
  searchValue,
  headerOffset = 200,
  width = "100%",
  needLoading = true,
  alignHeader,
  isTableFilterNeeded = false,
  tableSearchAction,
  tableSearchValue,
  headerStyleNeeded = true,
  extraAction = null,
  // optional: minimum rows before scrolling; adjust as needed
  autoScrollThreshold = 8,
}) => {
  const filteredColumns = columns?.filter((col) => col !== "-");
  const hasCheckboxColumn =
    filteredColumns.length > 0 &&
    (filteredColumns[0] === "" ||
      filteredColumns[0].toLowerCase().includes("select") ||
      filteredColumns[0].toLowerCase().includes("checkbox"));

  const [renderItems, setRenderItems] = useState([]);
  const [scrollableHeight, setScrollableHeight] = useState("500px");

  const headerClass = headerStyleNeeded
    ? alignHeader === "left"
      ? "px-4 py-3 text-left text-[15px] font-semibold bg-[rgb(26,42,68)] text-gray-100 dark:bg-[rgb(15,23,42)] border border-gray-300 dark:border-gray-700"
      : "px-4 py-3 text-center text-[15px] font-semibold bg-[rgb(26,42,68)] text-gray-100 dark:bg-[rgb(15,23,42)] border border-gray-300 dark:border-gray-700"
    : "px-4 py-3 text-center text-[15px]";

  useEffect(() => {
    // set a max height based on header offset
    const calculateScrollableHeight = () => {
      const offset = headerOffset || 200;
      const height = Math.max(240, window.innerHeight - offset); // min height 240
      setScrollableHeight(`${height}px`);
    };

    calculateScrollableHeight();
    window.addEventListener("resize", calculateScrollableHeight);
    return () => window.removeEventListener("resize", calculateScrollableHeight);
  }, [headerOffset]);

  useEffect(() => {
    setRenderItems(data ?? []);
  }, [data]);

  // helper returns renderCell result (unchanged) — we style via TD
  const getRenderedValue = (row, colIndex, col, rowIndex) => {
    if (!renderCell) return row[colIndex];
    try {
      return renderCell(row, colIndex, col, rowIndex);
    } catch (e) {
      // fallback to raw value if renderCell throws
      return row[colIndex];
    }
  };

  return (
    <div
      className="overflow-hidden border rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
      style={{ width }}
    >
      {/* Table filter / search */}
      {isTableFilterNeeded && (
        <div className="flex items-center justify-end gap-4 px-4 py-3
                        bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <input
            type="text"
            placeholder="Search..."
            value={tableSearchValue}
            onChange={(event) => tableSearchAction?.(event.target.value)}
            // NOTE: bg is light in light mode, dark in dark mode for better contrast
            className="w-64 px-3 py-2 text-[15px] border border-gray-300 dark:border-gray-700 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                       bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
          {extraAction && <div>{extraAction}</div>}
        </div>
      )}

      {/* SINGLE scroll container (avoid inner sticky scrollbar issues) */}
      <div
        className={`w-full`}
        style={{
          maxHeight: renderItems.length > autoScrollThreshold ? scrollableHeight : "auto",
          overflowY: renderItems.length > autoScrollThreshold ? "auto" : "visible",
        }}
      >
        {/* optional: thin scrollbar classes if you have the plugin (safe to keep) */}
        <table className="min-w-full text-[15px] border-collapse table-fixed">
          <colgroup>
            {filteredColumns.map((col, idx) => (
              <col
                key={idx}
                style={{
                  width:
                    hasCheckboxColumn && idx === 0
                      ? "40px"
                      : `${
                          hasCheckboxColumn
                            ? 100 / (filteredColumns.length - 1)
                            : 100 / filteredColumns.length
                        }%`,
                }}
              />
            ))}
          </colgroup>

          {/* Header: sticky */}
          <thead className="sticky top-0 z-20">
            <tr>
              {filteredColumns.map((col, idx) => (
                <th key={idx} className={headerClass}>
                  {col}
                </th>
              ))}
            </tr>

            {searchFilter && (
              <tr>
                <td
                  colSpan={filteredColumns.length}
                  className="p-2 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                >
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(evt) => searchAction?.(evt.target.value)}
                    className="w-full px-3 py-2 text-[15px] border border-gray-300 dark:border-gray-700 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                               bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </td>
              </tr>
            )}
          </thead>

          {/* Body */}
          <tbody>
            {needLoading ? (
              <tr>
                <td
                  colSpan={filteredColumns.length}
                  className="p-4 text-center text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700"
                >
                  <TailwindLoadingSpinner />
                </td>
              </tr>
            ) : renderItems.length === 0 ? (
              <tr>
                <td
                  colSpan={filteredColumns.length}
                  className="p-4 text-center text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700"
                >
                  No results found.
                </td>
              </tr>
            ) : (
              renderItems.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-[1.01] transition-transform cursor-pointer ${
                    isSelected?.(row, rowIndex)
                      ? "border-l-4 border-blue-500 dark:border-blue-400"
                      : ""
                  }`}
                >
                  {filteredColumns.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      // TD-level styles enforce font/color/vertical alignment and safe wrapping
                      className="px-4 py-3 text-[15px] leading-snug border border-gray-300 dark:border-gray-700
                                 text-gray-800 dark:text-gray-100 align-middle overflow-hidden"
                    >
                      {/* renderCell can return any HTML/JSX — TD ensures consistent styling */}
                      {getRenderedValue(row, colIndex, col, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TailwindTable;
